import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, Validators, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { CurrencyPipe, CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';

import { ModalCustomOptions, OTP_ACTION } from '@app/models';
import { UserService } from '@app/services';
import { ModalService } from '@app/services/modal/modal.service';
import { BankAccount } from '@interfaces/app';
import { CurrencyModule } from '@app/directives';

interface WithdrawSummary {
  label: string;
  value: string;
  highlight?: boolean;
  icon?: string;
}

@Component({
  selector: 'mnf-developer-withdraw',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    CurrencyModule,
  ],
  templateUrl: './developer-withdraw.component.html',
  styleUrls: ['./developer-withdraw.component.scss'],
  providers: [CurrencyPipe]
})
export class DeveloperWithdrawComponent implements OnInit, OnDestroy {
  balance: number;
  wdForm: FormGroup;
  totalAccounts: number;
  bankAccounts: BankAccount[];
  showConfirmation = false;
  isProcessing = false;
  withdrawSummary: WithdrawSummary[] = [];
  selectedAccountInfo: string = '';
  remainingBalance: number = 0;
  showInsufficientFunds = false;

  private destroy$ = new Subject<void>();

  modalOTPConf = {
    header: 'Retiro de fondos',
    title: 'Verificación de retiro',
    message: `Ingresa el <span class="cl-primary-7">Token</span> que ha sido enviado ${this.userServ.otpMethod === 'sms' ? 'por SMS' : 'a tu correo electrónico'}`
  };

  constructor(
    private modal: MatDialogRef<DeveloperWithdrawComponent>,
    private userServ: UserService,
    private modalServ: ModalService,
    private toastrServ: ToastrService,
    private currencyPipe: CurrencyPipe,
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.loadBankAccounts();
    this.setupFormListeners();
    this.remainingBalance = this.balance;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.wdForm = new FormGroup({
      bankAccount: new FormControl('', [Validators.required]),
      amount: new FormControl('', {
        validators: [
          Validators.required,
          Validators.min(1),
          Validators.max(this.balance),
          Validators.maxLength(18)
        ]
      }),
    });

    // Actualizar resumen cuando cambian los valores
    this.wdForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.showConfirmation) {
          this.updateWithdrawSummary();
        }
      });
  }

  private loadBankAccounts(): void {
    this.userServ.getBankAccounts()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (accounts) => {
          this.bankAccounts = accounts;
          this.totalAccounts = accounts.length;
          if (accounts.length > 0) {
            this.wdForm.controls.bankAccount.setValue(accounts[0]);
            this.updateSelectedAccountInfo(accounts[0]);
          }
        },
        error: (error) => {
          this.toastrServ.error('Error al cargar las cuentas bancarias');
          console.error('Error loading bank accounts:', error);
        }
      });
  }

  private setupFormListeners(): void {
    // Listener para cambios en el monto
    this.wdForm.controls.amount.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const amount = Number(value) || 0;
        this.remainingBalance = this.balance - amount;
        this.showInsufficientFunds = amount > this.balance;

        // Actualizar validación dinámica
        if (this.showInsufficientFunds) {
          this.wdForm.controls.amount.setErrors({ 'insufficientFunds': true });
        } else if (amount > 0 && amount <= this.balance) {
          const errors = this.wdForm.controls.amount.errors;
          if (errors?.['insufficientFunds']) {
            delete errors['insufficientFunds'];
            this.wdForm.controls.amount.setErrors(Object.keys(errors).length > 0 ? errors : null);
          }
        }
      });

    // Listener para cambios en la cuenta bancaria
    this.wdForm.controls.bankAccount.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(account => {
        if (account) {
          this.updateSelectedAccountInfo(account);
        }
      });
  }

  private updateSelectedAccountInfo(account: BankAccount): void {
    if (account) {
      this.selectedAccountInfo = `Banco: ${account.bankName} - Cuenta: **** ${account.clabe.slice(-4)}`;
    }
  }

  get control() {
    return this.wdForm.controls.amount;
  }

  formatCurrency(amount: number): string {
    return this.currencyPipe.transform(amount, 'MXN', 'symbol', '1.2-2') || '';
  }

  addAll(): void {
    this.wdForm.controls['amount'].setValue(this.balance);
  }

  close(): void {
    this.modal.close();
  }

  showConfirmationScreen(): void {
    if (this.wdForm.valid && !this.showInsufficientFunds) {
      const amount = Number(this.wdForm.value.amount);
      if (amount > this.balance) {
        this.toastrServ.warning('El monto a retirar no puede ser mayor al saldo disponible');
        return;
      }
      this.updateWithdrawSummary();
      this.showConfirmation = true;
    } else {
      this.markFormGroupTouched(this.wdForm);
      if (this.showInsufficientFunds) {
        this.toastrServ.warning('El monto a retirar no puede ser mayor al saldo disponible');
      }
    }
  }

  private updateWithdrawSummary(): void {
    const amount = Number(this.wdForm.value.amount);
    const bankAccount: BankAccount = this.wdForm.value.bankAccount;
    const newBalance = this.balance - amount;
    const withdrawDate = new Date();

    this.withdrawSummary = [
      {
        label: 'Monto a retirar',
        value: this.formatCurrency(amount),
        highlight: true,
        icon: 'assets/icon/wallet-outline.svg'
      },
      {
        label: 'Alias cuenta destino',
        value: `${bankAccount.alias}`,
        icon: 'assets/icons/credit-card-regular.svg'
      },
      {
         label: 'Banco destino',
         value: bankAccount.bankName,
         icon: 'assets/icons/credit-card-regular.svg'
      },
      {
        label: 'CLABE Destino',
        value: `**** **** **** ${bankAccount.clabe.slice(-4)}`,
        icon: 'assets/icons/credit-card-regular.svg'
      },
      {
        label: 'Titular de la cuenta',
        value: bankAccount?.titular || '',
        icon: 'assets/icons/address-card-regular.svg'
      },
      {
        label: 'Saldo actual',
        value: this.formatCurrency(this.balance)
      },
      {
        label: 'Saldo después del retiro',
        value: this.formatCurrency(newBalance),
        highlight: newBalance < 0
      },
      {
        label: 'Fecha de operación',
        value: this.formatDate(withdrawDate)
      },
      {
        label: 'Tiempo estimado',
        value: '24 a 48 horas hábiles'
      }
    ];
  }

  private formatDate(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return date.toLocaleDateString('es-MX', options);
  }

  backToForm(): void {
    this.showConfirmation = false;
  }

  confirmWithdraw(): void {
    if (this.wdForm.valid && !this.isProcessing) {
      this.isProcessing = true;
      const amount = Number(this.wdForm.value.amount);
      const bankAccount: BankAccount = this.wdForm.value.bankAccount;

      const conf: ModalCustomOptions = {
        header: 'Retiro de fondos',
        title: '¿Seguro de continuar?',
        message: `Vas a retirar un monto total de <span class="cl-primary-7">${this.formatCurrency(amount)} MXN</span>
        desde tu cuenta Monific a la cuenta: <span class="cl-primary-7">**** ${bankAccount.clabe} - ${bankAccount.bankName}</span>`,
        buttonText: 'Aceptar',
        cancelText: 'Cancelar'
      };

      const m = this.modalServ.modalCustom(conf);

      m.beforeClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (accept) => {
            if (accept) {
              this.openOTPModal(amount, bankAccount);
            } else {
              this.isProcessing = false;
              this.backToForm();
            }
          },
          error: () => {
            this.isProcessing = false;
          }
        });
    }
  }

  private openOTPModal(amount: number, bankAccount: BankAccount): void {
    this.modal.close();
    this.modalServ.sendAndOpenModalOTP(OTP_ACTION.SPO, this.modalOTPConf)
      .subscribe(otp => this.withdrawOption(otp, amount, bankAccount));
  }

  private withdrawOption(otp: string, amount: number, bankAccount: BankAccount): void {
    if (otp) {
      this.withdraw(otp, amount, bankAccount);
    } else {
      this.modalServ.openDeveloperWithdraw(this.balance);
    }
  }

  private withdraw(otp: string, amount: number, bankAccount: BankAccount): void {
    this.userServ.createSpoOut(otp, amount, bankAccount.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.modalServ.modalCustom({
            img: 'https://static.monific.com/assets/assets-app/img/icon/img/funds-success.svg',
            title: 'Retiro exitoso',
            message: `Retiro por <span class="cl-primary"><strong>${this.formatCurrency(amount)}</strong></span> de tu cuenta Monific a la cuenta <span class="cl-primary fw-sbold"><strong>****${bankAccount.clabe} - ${bankAccount.bankName}</strong></span>`,
            buttonText: 'Aceptar'
          }).afterClosed().subscribe(() => {
              window.location.reload();
          });
        },
        error: (err) => {
         console.log('Error al procesar el retiro:', err);

          this.isProcessing = false;
          if (err.error?.err !== 'TS01') {
            this.toastrServ.warning(err.error?.msg || 'Error al procesar el retiro');
          }
          this.modalServ.openModalOTP(this.modalOTPConf)
            .pipe(takeUntil(this.destroy$))
            .subscribe(otp => this.withdrawOption(otp, amount, bankAccount));
        }
      });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  get estimatedFee(): number {
    // Comisión estimada (puede ajustarse según las reglas de negocio)
    return 0;
  }

  get totalAmount(): number {
    const amount = Number(this.wdForm.value.amount) || 0;
    return amount + this.estimatedFee;
  }
}
